/**
 * Procedural sounds (W1-14): the fallback hitsounds used when the active skin has no click/drag/flick audio
 * (S§8: the editor never depends on proprietary assets), and the metronome / calibration clicks.
 *
 * Pure PCM generators (mono Float32Array, deterministic: a seeded PRNG, no Math.random), so they run in Node
 * tests. Every sound starts at sample 0 with its loudest transient within the first millisecond, so scheduling a
 * sound at a note time puts its audible onset at that time (S§1.4: skin sounds have no leading silence either).
 */

export type SynthSound = 'click' | 'drag' | 'flick' | 'metronome' | 'metronomeAccent';

/** mulberry32: tiny deterministic PRNG returning floats in [0, 1). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function frames(sampleRate: number, seconds: number): number {
  return Math.max(1, Math.round(sampleRate * seconds));
}

/** Scales `data` in place so that its absolute peak equals `peak` (no-op for silence). */
function normalize(data: Float32Array, peak: number): Float32Array {
  let max = 0;
  for (const v of data) max = Math.max(max, Math.abs(v));
  if (max > 0) {
    const k = peak / max;
    for (let i = 0; i < data.length; i++) data[i] = (data[i] ?? 0) * k;
  }
  return data;
}

/** Short fade at the very end so a sound never stops on a discontinuity. */
function fadeTail(data: Float32Array, sampleRate: number, seconds = 0.004): void {
  const n = Math.min(data.length, frames(sampleRate, seconds));
  for (let i = 0; i < n; i++) {
    const idx = data.length - 1 - i;
    data[idx] = (data[idx] ?? 0) * (i / n);
  }
}

/** Tap / Hold head: a bright percussive tick with a short body. */
function click(sampleRate: number): Float32Array {
  const n = frames(sampleRate, 0.07);
  const out = new Float32Array(n);
  const noise = rng(0x5eed01);
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const tick = Math.sin(2 * Math.PI * 1800 * t) * Math.exp(-t / 0.008);
    const body = 0.6 * Math.sin(2 * Math.PI * 900 * t) * Math.exp(-t / 0.016);
    const snap = 0.5 * (noise() * 2 - 1) * Math.exp(-t / 0.0015);
    out[i] = tick + body + snap;
  }
  fadeTail(out, sampleRate);
  return normalize(out, 0.8);
}

/** Drag: lighter and higher than the click. */
function drag(sampleRate: number): Float32Array {
  const n = frames(sampleRate, 0.05);
  const out = new Float32Array(n);
  const noise = rng(0x5eed02);
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const tone = Math.sin(2 * Math.PI * 2600 * t) * Math.exp(-t / 0.006);
    const air = 0.25 * (noise() * 2 - 1) * Math.exp(-t / 0.002);
    out[i] = tone + air;
  }
  fadeTail(out, sampleRate);
  return normalize(out, 0.55);
}

/** Flick: an upward "swish" (band-passed noise sweeping 1.5 -> 5 kHz) on top of a timing transient. */
function flick(sampleRate: number): Float32Array {
  const dur = 0.12;
  const n = frames(sampleRate, dur);
  const out = new Float32Array(n);
  const noise = rng(0x5eed03);
  // Time-varying two-pole resonator (constant-skirt band-pass).
  let y1 = 0;
  let y2 = 0;
  const q = 4;
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const u = t / dur;
    const fc = Math.min(1500 * Math.pow(5000 / 1500, u), sampleRate * 0.45);
    const w = (2 * Math.PI * fc) / sampleRate;
    const alpha = Math.sin(w) / (2 * q);
    const a0 = 1 + alpha;
    const b0 = alpha / a0;
    const a1 = (-2 * Math.cos(w)) / a0;
    const a2 = (1 - alpha) / a0;
    const x = noise() * 2 - 1;
    const y = b0 * x - a1 * y1 - a2 * y2;
    y2 = y1;
    y1 = y;
    const env = Math.min(1, t / 0.003) * Math.pow(1 - u, 1.5);
    const transient = 0.8 * Math.sin(2 * Math.PI * 2200 * t) * Math.exp(-t / 0.004);
    out[i] = 3 * y * env + transient;
  }
  fadeTail(out, sampleRate);
  return normalize(out, 0.7);
}

/** Metronome click: a sine burst with a third harmonic for definition; accents are higher and louder. */
function metronome(sampleRate: number, accent: boolean): Float32Array {
  const n = frames(sampleRate, 0.05);
  const out = new Float32Array(n);
  const f = accent ? 2000 : 1300;
  for (let i = 0; i < n; i++) {
    const t = i / sampleRate;
    const env = Math.exp(-t / 0.01);
    out[i] = (Math.sin(2 * Math.PI * f * t) + 0.25 * Math.sin(2 * Math.PI * 3 * f * t)) * env;
  }
  fadeTail(out, sampleRate);
  return normalize(out, accent ? 0.85 : 0.6);
}

/** Mono PCM for a procedural sound at `sampleRate` (deterministic). */
export function synthesize(sound: SynthSound, sampleRate: number): Float32Array {
  if (!(sampleRate > 0) || !Number.isFinite(sampleRate))
    throw new RangeError(`synthesize: bad sample rate ${sampleRate}`);
  switch (sound) {
    case 'click':
      return click(sampleRate);
    case 'drag':
      return drag(sampleRate);
    case 'flick':
      return flick(sampleRate);
    case 'metronome':
      return metronome(sampleRate, false);
    case 'metronomeAccent':
      return metronome(sampleRate, true);
  }
}

/** Minimal factory surface shared by AudioContext and OfflineAudioContext. */
export interface BufferFactory {
  readonly sampleRate: number;
  createBuffer(numberOfChannels: number, length: number, sampleRate: number): AudioBuffer;
}

/** Wraps mono PCM in an AudioBuffer of the factory's sample rate. */
export function pcmToBuffer(factory: BufferFactory, pcm: Float32Array, sampleRate: number): AudioBuffer {
  const buffer = factory.createBuffer(1, pcm.length, sampleRate);
  buffer.getChannelData(0).set(pcm);
  return buffer;
}

/** A procedural sound as an AudioBuffer at the context's sample rate. */
export function synthBuffer(factory: BufferFactory, sound: SynthSound): AudioBuffer {
  return pcmToBuffer(factory, synthesize(sound, factory.sampleRate), factory.sampleRate);
}
